import { useState, useEffect } from "react";
import useAPI from "../hooks/useAPI";

export default function useAdminSalesStats() {
  const [periodType, setPeriodType] = useState("30days");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({
    periodType: "30days",
    startDate: null,
    endDate: null,
  });
  const [stats, setStats] = useState({
    totalSales: 0,
    totalRevenue: 0,
    byDate: [],
    byUser: [],
    trend: 0,
    averagePerDay: 0,
  });

  // Charger toutes les ventes une seule fois
  const { data, query: fetchData } = useAPI({
    type: "get",
    url: "payments",
    defer: false,
  });

  // Debug: afficher la structure des données reçues
  useEffect(() => {
    if (data) {
      console.log("📊 Données reçues de l'API payments:", data);
      console.log("📊 Structure:", Object.keys(data));
    }
  }, [data]);

  // Calculer les dates selon le type de période
  const calculateDates = (type) => {
    const end = new Date();
    const start = new Date();

    switch (type) {
      case "7days":
        start.setDate(end.getDate() - 6);
        break;
      case "30days":
        start.setDate(end.getDate() - 29);
        break;
      case "3months":
        start.setMonth(end.getMonth() - 3);
        break;
      case "year":
        start.setFullYear(end.getFullYear() - 1);
        break;
      default:
        break;
    }

    return { start, end };
  };

  // Calculer les statistiques à partir des ventes filtrées
  const calculateStats = (sales) => {
    if (!sales || !appliedFilters.startDate || !appliedFilters.endDate) {
      return {
        totalSales: 0,
        totalRevenue: 0,
        byDate: [],
        byUser: [],
        trend: 0,
        averagePerDay: 0,
      };
    }

    // Debug: voir la structure d'une vente
    if (sales.length > 0) {
      console.log("🔍 Structure d'une vente (premier élément):", sales[0]);
      console.log("🔍 Champs disponibles:", Object.keys(sales[0]));
    }

    // Filtrer les ventes par date
    const startTime = new Date(appliedFilters.startDate);
    startTime.setHours(0, 0, 0, 0); // Début de la journée
    const endTime = new Date(appliedFilters.endDate);
    endTime.setHours(23, 59, 59, 999); // Fin de la journée

    console.log("📅 Plage de dates:", { startTime, endTime });

    const filteredSales = sales.filter((sale, index) => {
      const saleDate = new Date(sale.saleCreated);
      
      // Debug pour les premières ventes
      if (index < 3) {
        console.log(`🔍 Vente ${index}:`, {
          saleCreated: sale.saleCreated,
          parsedDate: saleDate,
          isInRange: saleDate >= startTime && saleDate <= endTime
        });
      }
      
      return saleDate >= startTime && saleDate <= endTime;
    });

    console.log(`✅ Ventes filtrées: ${filteredSales.length} sur ${sales.length}`);

    // Total des ventes
    const totalSales = filteredSales.length;

    // Revenu total
    const totalRevenue = filteredSales.reduce((sum, sale) => {
      return sum + (parseFloat(sale.saleAmount) || 0);
    }, 0);

    // Grouper par date
    const byDateMap = {};
    filteredSales.forEach((sale) => {
      const date = new Date(sale.saleCreated)
        .toISOString()
        .split("T")[0];
      if (!byDateMap[date]) {
        byDateMap[date] = { count: 0, revenue: 0 };
      }
      byDateMap[date].count++;
      byDateMap[date].revenue += parseFloat(sale.saleAmount) || 0;
    });

    const byDate = Object.entries(byDateMap)
      .map(([date, data]) => ({
        date,
        count: data.count,
        revenue: data.revenue,
      }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    // Grouper par utilisateur
    const byUserMap = {};
    filteredSales.forEach((sale) => {
      // User peut être un ID ou un objet, on prend l'ID
      const userId = typeof sale.User === 'object' ? (sale.User?.id || 'unknown') : (sale.User || "unknown");
      
      // Construire le nom à partir des champs séparés
      let userName = "Utilisateur inconnu";
      if (sale.userFirstname || sale.userLastname) {
        userName = `${sale.userFirstname || ''} ${sale.userLastname || ''}`.trim();
      } else if (sale.userEmail) {
        userName = sale.userEmail;
      }
      
      if (!byUserMap[userId]) {
        byUserMap[userId] = {
          userId,
          userName,
          count: 0,
          revenue: 0,
        };
      }
      byUserMap[userId].count++;
      byUserMap[userId].revenue += parseFloat(sale.saleAmount) || 0;
    });

    const byUser = Object.values(byUserMap)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Calculer la tendance (différence avec période précédente)
    const periodDays = Math.ceil((endTime - startTime) / (1000 * 60 * 60 * 24));
    const previousStart = new Date(startTime);
    previousStart.setDate(previousStart.getDate() - periodDays);
    const previousEnd = new Date(startTime);
    previousEnd.setDate(previousEnd.getDate() - 1);

    const previousSales = sales.filter((sale) => {
      const saleDate = new Date(sale.saleCreated);
      return saleDate >= previousStart && saleDate <= previousEnd;
    });

    const trend =
      previousSales.length > 0
        ? ((totalSales - previousSales.length) / previousSales.length) * 100
        : 0;

    // Moyenne par jour
    const averagePerDay = periodDays > 0 ? totalSales / periodDays : 0;

    return {
      totalSales,
      totalRevenue,
      byDate,
      byUser,
      trend,
      averagePerDay,
    };
  };

  // Initialiser les dates au montage
  useEffect(() => {
    const dates = calculateDates(periodType);
    setStartDate(dates.start);
    setEndDate(dates.end);
    setAppliedFilters({
      periodType,
      startDate: dates.start,
      endDate: dates.end,
    });
  }, []);

  // Recalculer les stats quand les données ou les filtres changent
  useEffect(() => {
    console.log("🔄 useEffect déclenché - data:", data);
    console.log("🔄 Filtres appliqués:", appliedFilters);

    if (data) {
      // Essayer différentes structures possibles
      let salesData = null;
      if (data.sales) {
        console.log("✅ Trouvé data.sales:", data.sales.length, "ventes");
        salesData = data.sales;
      } else if (Array.isArray(data)) {
        console.log("✅ data est un tableau direct:", data.length, "ventes");
        salesData = data;
      } else if (data.payments) {
        console.log("✅ Trouvé data.payments:", data.payments.length, "ventes");
        salesData = data.payments;
      } else {
        console.log(
          "❌ Structure inconnue - Clés disponibles:",
          Object.keys(data)
        );
      }

      if (salesData) {
        const newStats = calculateStats(salesData);
        console.log("📊 Stats calculées:", newStats);
        setStats(newStats);
      }
    } else {
      console.log("❌ Pas de données disponibles");
    }
  }, [data, appliedFilters]);

  const handlePeriodTypeChange = (type) => {
    setPeriodType(type);
    if (type !== "custom") {
      const dates = calculateDates(type);
      setStartDate(dates.start);
      setEndDate(dates.end);
    } else {
      // En mode custom, s'assurer que les dates sont définies
      if (!startDate || !endDate) {
        const dates = calculateDates("30days");
        setStartDate(dates.start);
        setEndDate(dates.end);
      }
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      periodType,
      startDate,
      endDate,
    });
  };

  const handleRefresh = async () => {
    await fetchData();
  };

  return {
    periodType,
    startDate,
    endDate,
    stats,
    loading: !data,
    handlePeriodTypeChange,
    handleStartDateChange: setStartDate,
    handleEndDateChange: setEndDate,
    handleApplyFilters,
    handleRefresh,
  };
}
